import type {
  PromotionClaimEvaluation,
  PromotionClaimEvidence,
} from "../promotion/promotion-claim-session";

export type PosSaleMember = {
  customer_id: string;
  membership_id: string;
  customer_code: string;
  customer_name: string;
  phone: string | null;
  email: string | null;
  membership_status: string;
  joined_at: string | null;
  identifiers: Array<{ identifier_type: string; display_value: string | null; is_primary: boolean }>;
};

export type PosStaffClaimPromotion = {
  promotion_policy_id: string;
  promotion_version_id: string;
  policy_code: string;
  policy_name: string;
  activation_code: "STAFF_CLAIM";
  offline_capability: string;
  effective_from: string | null;
  effective_to: string | null;
};

export type AppliedPromotion = {
  policy_id: string;
  promotion_version_id: string;
  policy_code: string;
  policy_name: string;
  activation_code: string;
  reason_code: string;
  requested_discount_amount: number;
  applied_discount_amount: number;
  loyalty_earn_treatment?: "FULL_POINTS" | "HALF_POINTS" | "NO_POINTS" | string;
  condition_document?: Record<string, unknown>;
  benefit_document?: Array<{ code: string; params?: Record<string, unknown> }>;
  offline_capability?: string;
  line_allocations: Array<{ line_id: string; amount: number; applied_quantity?: number }>;
};

export type PosSaleQuote = {
  ready: boolean;
  currency_code: string;
  currency_scale: number;
  evaluation_at: string;
  quote_fingerprint: string;
  amounts: {
    pre_promotion_total: number;
    promotion_discount_total: number;
    post_promotion_total: number;
  };
  promotion_result: {
    applied_promotions: AppliedPromotion[];
    blocked_promotions: Array<Record<string, unknown>>;
    promotion_discount_total: number;
    pre_promotion_total: number;
    post_promotion_total: number;
  };
  promotion_claim_evaluations?: PromotionClaimEvaluation[];
};

export type QuoteLine = {
  line_id: string;
  menu_item_id: string;
  menu_variant_id: string | null;
  quantity: number;
  modifier_option_ids: string[];
};

type GatewayResult<T> = { ok: boolean; data?: T; error?: string };

export class PosSaleContextGatewayClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;

  constructor(options: { baseUrl: string; timeoutMs?: number }) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.timeoutMs = options.timeoutMs ?? 15_000;
  }

  private async post<T>(path: string, body: Record<string, unknown>): Promise<GatewayResult<T>> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), this.timeoutMs);
    try {
      const response = await fetch(`${this.baseUrl}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "omit",
        signal: controller.signal,
      });
      const payload = (await response.json()) as GatewayResult<T>;
      return response.ok ? payload : { ok: false, error: payload.error ?? `GATEWAY_HTTP_${response.status}` };
    } catch (error) {
      return { ok: false, error: error instanceof DOMException && error.name === "AbortError" ? "GATEWAY_TIMEOUT" : "GATEWAY_UNREACHABLE" };
    } finally {
      window.clearTimeout(timeout);
    }
  }

  memberSearch(input: { deviceSecret: string; sessionToken: string; searchText: string; limit?: number }) {
    return this.post<{ ok: boolean; contract: string; items: PosSaleMember[] }>("/api/pos/runtime/member-search", {
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      search_text: input.searchText,
      limit: input.limit ?? 20,
    });
  }

  quote(input: {
    deviceSecret: string;
    sessionToken: string;
    lines: QuoteLine[];
    claimedPromotionVersionIds: string[];
    promotionClaimEvidence?: PromotionClaimEvidence[];
    saleContext?: { customerId: string; membershipId: string } | null;
    evaluationAt?: string;
  }) {
    return this.post<{ ok: boolean; contract: string; quote: PosSaleQuote; staff_claim_options: PosStaffClaimPromotion[] }>("/api/pos/runtime/sale-quote", {
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      lines: input.lines,
      claimed_promotion_version_ids: input.claimedPromotionVersionIds,
      promotion_claim_evidence: input.promotionClaimEvidence ?? [],
      sale_context: {
        customer_id: input.saleContext?.customerId ?? null,
        membership_id: input.saleContext?.membershipId ?? null,
      },
      evaluation_at: input.evaluationAt ?? new Date().toISOString(),
      execution_mode: "ONLINE",
    });
  }
}
